/**
 * Lista completa de 184 idiomas suportados pelo WhiteLabelClaw
 * Cada idioma tem: código, nome em inglês, nome nativo, bandeira (emoji), região
 * 
 * Fonte: ISO 639-1 + variantes regionais comuns
 * Referência: CLDR (Unicode Common Locale Data Repository)
 */

export interface Language {
  code: string;        // Código BCP 47 (ex: pt-BR, en-US, ja)
  name: string;        // Nome em inglês
  nativeName: string;  // Nome no idioma nativo
  flag: string;        // Emoji de bandeira
  region: string;      // Região/grupo
  rtl?: boolean;       // Right-to-left (árabe, hebraico, etc)
}

export const SUPPORTED_LANGUAGES: Language[] = [
  // ══════════════════════════════════════════
  // WAVE 0 — Base (Ativos por padrão)
  // ══════════════════════════════════════════
  { code: 'pt-BR', name: 'Portuguese (Brazil)', nativeName: 'Português (Brasil)', flag: '🇧🇷', region: 'Americas' },
  { code: 'en-US', name: 'English (US)', nativeName: 'English (US)', flag: '🇺🇸', region: 'Americas' },
  { code: 'en-GB', name: 'English (UK)', nativeName: 'English (UK)', flag: '🇬🇧', region: 'Europe' },
  { code: 'es-ES', name: 'Spanish (Spain)', nativeName: 'Español (España)', flag: '🇪🇸', region: 'Europe' },
  { code: 'es-MX', name: 'Spanish (Mexico)', nativeName: 'Español (México)', flag: '🇲🇽', region: 'Americas' },
  { code: 'es-AR', name: 'Spanish (Argentina)', nativeName: 'Español (Argentina)', flag: '🇦🇷', region: 'Americas' },
  { code: 'es-CO', name: 'Spanish (Colombia)', nativeName: 'Español (Colombia)', flag: '🇨🇴', region: 'Americas' },
  { code: 'es-CL', name: 'Spanish (Chile)', nativeName: 'Español (Chile)', flag: '🇨🇱', region: 'Americas' },
  { code: 'es-PE', name: 'Spanish (Peru)', nativeName: 'Español (Perú)', flag: '🇵🇪', region: 'Americas' },
  { code: 'pt-PT', name: 'Portuguese (Portugal)', nativeName: 'Português (Portugal)', flag: '🇵🇹', region: 'Europe' },

  // ══════════════════════════════════════════
  // WAVE 1 — Europa Ocidental
  // ══════════════════════════════════════════
  { code: 'fr-FR', name: 'French (France)', nativeName: 'Français (France)', flag: '🇫🇷', region: 'Europe' },
  { code: 'fr-CA', name: 'French (Canada)', nativeName: 'Français (Canada)', flag: '🇨🇦', region: 'Americas' },
  { code: 'fr-BE', name: 'French (Belgium)', nativeName: 'Français (Belgique)', flag: '🇧🇪', region: 'Europe' },
  { code: 'fr-CH', name: 'French (Switzerland)', nativeName: 'Français (Suisse)', flag: '🇨🇭', region: 'Europe' },
  { code: 'de-DE', name: 'German (Germany)', nativeName: 'Deutsch (Deutschland)', flag: '🇩🇪', region: 'Europe' },
  { code: 'de-AT', name: 'German (Austria)', nativeName: 'Deutsch (Österreich)', flag: '🇦🇹', region: 'Europe' },
  { code: 'de-CH', name: 'German (Switzerland)', nativeName: 'Deutsch (Schweiz)', flag: '🇨🇭', region: 'Europe' },
  { code: 'it-IT', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹', region: 'Europe' },
  { code: 'nl-NL', name: 'Dutch (Netherlands)', nativeName: 'Nederlands', flag: '🇳🇱', region: 'Europe' },
  { code: 'nl-BE', name: 'Dutch (Belgium)', nativeName: 'Nederlands (België)', flag: '🇧🇪', region: 'Europe' },
  { code: 'ca', name: 'Catalan', nativeName: 'Català', flag: '🏴', region: 'Europe' },
  { code: 'gl', name: 'Galician', nativeName: 'Galego', flag: '🏴', region: 'Europe' },
  { code: 'eu', name: 'Basque', nativeName: 'Euskara', flag: '🏴', region: 'Europe' },
  { code: 'lb', name: 'Luxembourgish', nativeName: 'Lëtzebuergesch', flag: '🇱🇺', region: 'Europe' },

  // ══════════════════════════════════════════
  // WAVE 2 — Norte da Europa (Escandinávia + Bálticos)
  // ══════════════════════════════════════════
  { code: 'sv-SE', name: 'Swedish', nativeName: 'Svenska', flag: '🇸🇪', region: 'Europe' },
  { code: 'da-DK', name: 'Danish', nativeName: 'Dansk', flag: '🇩🇰', region: 'Europe' },
  { code: 'nb-NO', name: 'Norwegian Bokmål', nativeName: 'Norsk bokmål', flag: '🇳🇴', region: 'Europe' },
  { code: 'nn-NO', name: 'Norwegian Nynorsk', nativeName: 'Norsk nynorsk', flag: '🇳🇴', region: 'Europe' },
  { code: 'fi-FI', name: 'Finnish', nativeName: 'Suomi', flag: '🇫🇮', region: 'Europe' },
  { code: 'is', name: 'Icelandic', nativeName: 'Íslenska', flag: '🇮🇸', region: 'Europe' },
  { code: 'fo', name: 'Faroese', nativeName: 'Føroyskt', flag: '🇫🇴', region: 'Europe' },
  { code: 'et-EE', name: 'Estonian', nativeName: 'Eesti', flag: '🇪🇪', region: 'Europe' },
  { code: 'lv-LV', name: 'Latvian', nativeName: 'Latviešu', flag: '🇱🇻', region: 'Europe' },
  { code: 'lt-LT', name: 'Lithuanian', nativeName: 'Lietuvių', flag: '🇱🇹', region: 'Europe' },

  // ══════════════════════════════════════════
  // WAVE 3 — Europa Central & Oriental
  // ══════════════════════════════════════════
  { code: 'pl-PL', name: 'Polish', nativeName: 'Polski', flag: '🇵🇱', region: 'Europe' },
  { code: 'cs-CZ', name: 'Czech', nativeName: 'Čeština', flag: '🇨🇿', region: 'Europe' },
  { code: 'sk-SK', name: 'Slovak', nativeName: 'Slovenčina', flag: '🇸🇰', region: 'Europe' },
  { code: 'hu-HU', name: 'Hungarian', nativeName: 'Magyar', flag: '🇭🇺', region: 'Europe' },
  { code: 'ro-RO', name: 'Romanian', nativeName: 'Română', flag: '🇷🇴', region: 'Europe' },
  { code: 'bg-BG', name: 'Bulgarian', nativeName: 'Български', flag: '🇧🇬', region: 'Europe' },
  { code: 'hr-HR', name: 'Croatian', nativeName: 'Hrvatski', flag: '🇭🇷', region: 'Europe' },
  { code: 'sr-Latn', name: 'Serbian (Latin)', nativeName: 'Srpski', flag: '🇷🇸', region: 'Europe' },
  { code: 'sr-Cyrl', name: 'Serbian (Cyrillic)', nativeName: 'Српски', flag: '🇷🇸', region: 'Europe' },
  { code: 'bs-BA', name: 'Bosnian', nativeName: 'Bosanski', flag: '🇧🇦', region: 'Europe' },
  { code: 'sl-SI', name: 'Slovenian', nativeName: 'Slovenščina', flag: '🇸🇮', region: 'Europe' },
  { code: 'mk', name: 'Macedonian', nativeName: 'Македонски', flag: '🇲🇰', region: 'Europe' },
  { code: 'sq-AL', name: 'Albanian', nativeName: 'Shqip', flag: '🇦🇱', region: 'Europe' },
  { code: 'el-GR', name: 'Greek', nativeName: 'Ελληνικά', flag: '🇬🇷', region: 'Europe' },
  { code: 'mt', name: 'Maltese', nativeName: 'Malti', flag: '🇲🇹', region: 'Europe' },

  // ══════════════════════════════════════════
  // WAVE 4 — Leste Europeu & Eurásia
  // ══════════════════════════════════════════
  { code: 'ru-RU', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺', region: 'Europe' },
  { code: 'uk-UA', name: 'Ukrainian', nativeName: 'Українська', flag: '🇺🇦', region: 'Europe' },
  { code: 'be', name: 'Belarusian', nativeName: 'Беларуская', flag: '🇧🇾', region: 'Europe' },
  { code: 'ka-GE', name: 'Georgian', nativeName: 'ქართული', flag: '🇬🇪', region: 'Europe' },
  { code: 'hy-AM', name: 'Armenian', nativeName: 'Հայերեն', flag: '🇦🇲', region: 'Europe' },
  { code: 'az-AZ', name: 'Azerbaijani', nativeName: 'Azərbaycan', flag: '🇦🇿', region: 'Asia' },
  { code: 'kk-KZ', name: 'Kazakh', nativeName: 'Қазақ', flag: '🇰🇿', region: 'Asia' },
  { code: 'uz-UZ', name: 'Uzbek', nativeName: 'Oʻzbek', flag: '🇺🇿', region: 'Asia' },
  { code: 'ky-KG', name: 'Kyrgyz', nativeName: 'Кыргызча', flag: '🇰🇬', region: 'Asia' },
  { code: 'tk', name: 'Turkmen', nativeName: 'Türkmen', flag: '🇹🇲', region: 'Asia' },
  { code: 'tg', name: 'Tajik', nativeName: 'Тоҷикӣ', flag: '🇹🇯', region: 'Asia' },
  { code: 'mn', name: 'Mongolian', nativeName: 'Монгол', flag: '🇲🇳', region: 'Asia' },

  // ══════════════════════════════════════════
  // WAVE 5 — Oriente Médio & Norte da África (RTL!)
  // ══════════════════════════════════════════
  { code: 'ar-SA', name: 'Arabic (Saudi)', nativeName: 'العربية (السعودية)', flag: '🇸🇦', region: 'Middle East', rtl: true },
  { code: 'ar-EG', name: 'Arabic (Egypt)', nativeName: 'العربية (مصر)', flag: '🇪🇬', region: 'Middle East', rtl: true },
  { code: 'ar-AE', name: 'Arabic (UAE)', nativeName: 'العربية (الإمارات)', flag: '🇦🇪', region: 'Middle East', rtl: true },
  { code: 'ar-MA', name: 'Arabic (Morocco)', nativeName: 'العربية (المغرب)', flag: '🇲🇦', region: 'Africa', rtl: true },
  { code: 'ar-DZ', name: 'Arabic (Algeria)', nativeName: 'العربية (الجزائر)', flag: '🇩🇿', region: 'Africa', rtl: true },
  { code: 'ar-TN', name: 'Arabic (Tunisia)', nativeName: 'العربية (تونس)', flag: '🇹🇳', region: 'Africa', rtl: true },
  { code: 'ar-IQ', name: 'Arabic (Iraq)', nativeName: 'العربية (العراق)', flag: '🇮🇶', region: 'Middle East', rtl: true },
  { code: 'ar-JO', name: 'Arabic (Jordan)', nativeName: 'العربية (الأردن)', flag: '🇯🇴', region: 'Middle East', rtl: true },
  { code: 'ar-LB', name: 'Arabic (Lebanon)', nativeName: 'العربية (لبنان)', flag: '🇱🇧', region: 'Middle East', rtl: true },
  { code: 'he-IL', name: 'Hebrew', nativeName: 'עברית', flag: '🇮🇱', region: 'Middle East', rtl: true },
  { code: 'fa-IR', name: 'Persian (Farsi)', nativeName: 'فارسی', flag: '🇮🇷', region: 'Middle East', rtl: true },
  { code: 'ku', name: 'Kurdish (Kurmanji)', nativeName: 'Kurdî', flag: '🏴', region: 'Middle East' },
  { code: 'ckb', name: 'Kurdish (Sorani)', nativeName: 'سۆرانی', flag: '🏴', region: 'Middle East', rtl: true },
  { code: 'ps', name: 'Pashto', nativeName: 'پښتو', flag: '🇦🇫', region: 'Asia', rtl: true },
  { code: 'ur-PK', name: 'Urdu', nativeName: 'اردو', flag: '🇵🇰', region: 'Asia', rtl: true },
  { code: 'tr-TR', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷', region: 'Middle East' },

  // ══════════════════════════════════════════
  // WAVE 6 — Ásia Oriental
  // ══════════════════════════════════════════
  { code: 'ja-JP', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵', region: 'Asia' },
  { code: 'ko-KR', name: 'Korean', nativeName: '한국어', flag: '🇰🇷', region: 'Asia' },
  { code: 'zh-CN', name: 'Chinese (Simplified)', nativeName: '简体中文', flag: '🇨🇳', region: 'Asia' },
  { code: 'zh-TW', name: 'Chinese (Traditional)', nativeName: '繁體中文', flag: '🇹🇼', region: 'Asia' },
  { code: 'zh-HK', name: 'Chinese (Hong Kong)', nativeName: '中文 (香港)', flag: '🇭🇰', region: 'Asia' },

  // ══════════════════════════════════════════
  // WAVE 7 — Sul da Ásia
  // ══════════════════════════════════════════
  { code: 'hi-IN', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳', region: 'Asia' },
  { code: 'bn-BD', name: 'Bengali (Bangladesh)', nativeName: 'বাংলা', flag: '🇧🇩', region: 'Asia' },
  { code: 'bn-IN', name: 'Bengali (India)', nativeName: 'বাংলা (ভারত)', flag: '🇮🇳', region: 'Asia' },
  { code: 'pa-IN', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ', flag: '🇮🇳', region: 'Asia' },
  { code: 'gu-IN', name: 'Gujarati', nativeName: 'ગુજરાતી', flag: '🇮🇳', region: 'Asia' },
  { code: 'mr-IN', name: 'Marathi', nativeName: 'मराठी', flag: '🇮🇳', region: 'Asia' },
  { code: 'ta-IN', name: 'Tamil', nativeName: 'தமிழ்', flag: '🇮🇳', region: 'Asia' },
  { code: 'te-IN', name: 'Telugu', nativeName: 'తెలుగు', flag: '🇮🇳', region: 'Asia' },
  { code: 'kn-IN', name: 'Kannada', nativeName: 'ಕನ್ನಡ', flag: '🇮🇳', region: 'Asia' },
  { code: 'ml-IN', name: 'Malayalam', nativeName: 'മലയാളം', flag: '🇮🇳', region: 'Asia' },
  { code: 'or-IN', name: 'Odia', nativeName: 'ଓଡ଼ିଆ', flag: '🇮🇳', region: 'Asia' },
  { code: 'as', name: 'Assamese', nativeName: 'অসমীয়া', flag: '🇮🇳', region: 'Asia' },
  { code: 'ne-NP', name: 'Nepali', nativeName: 'नेपाली', flag: '🇳🇵', region: 'Asia' },
  { code: 'si-LK', name: 'Sinhala', nativeName: 'සිංහල', flag: '🇱🇰', region: 'Asia' },
  { code: 'dv', name: 'Dhivehi', nativeName: 'ދިވެހި', flag: '🇲🇻', region: 'Asia', rtl: true },

  // ══════════════════════════════════════════
  // WAVE 8 — Sudeste Asiático
  // ══════════════════════════════════════════
  { code: 'th-TH', name: 'Thai', nativeName: 'ไทย', flag: '🇹🇭', region: 'Asia' },
  { code: 'vi-VN', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳', region: 'Asia' },
  { code: 'id-ID', name: 'Indonesian', nativeName: 'Bahasa Indonesia', flag: '🇮🇩', region: 'Asia' },
  { code: 'ms-MY', name: 'Malay', nativeName: 'Bahasa Melayu', flag: '🇲🇾', region: 'Asia' },
  { code: 'tl-PH', name: 'Filipino/Tagalog', nativeName: 'Filipino', flag: '🇵🇭', region: 'Asia' },
  { code: 'my', name: 'Burmese', nativeName: 'မြန်မာ', flag: '🇲🇲', region: 'Asia' },
  { code: 'km-KH', name: 'Khmer', nativeName: 'ខ្មែរ', flag: '🇰🇭', region: 'Asia' },
  { code: 'lo-LA', name: 'Lao', nativeName: 'ລາວ', flag: '🇱🇦', region: 'Asia' },
  { code: 'jv', name: 'Javanese', nativeName: 'Basa Jawa', flag: '🇮🇩', region: 'Asia' },
  { code: 'su', name: 'Sundanese', nativeName: 'Basa Sunda', flag: '🇮🇩', region: 'Asia' },
  { code: 'ceb', name: 'Cebuano', nativeName: 'Cebuano', flag: '🇵🇭', region: 'Asia' },

  // ══════════════════════════════════════════
  // WAVE 9 — África Subsaariana
  // ══════════════════════════════════════════
  { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili', flag: '🇰🇪', region: 'Africa' },
  { code: 'am-ET', name: 'Amharic', nativeName: 'አማርኛ', flag: '🇪🇹', region: 'Africa' },
  { code: 'ha', name: 'Hausa', nativeName: 'Hausa', flag: '🇳🇬', region: 'Africa' },
  { code: 'yo', name: 'Yoruba', nativeName: 'Yorùbá', flag: '🇳🇬', region: 'Africa' },
  { code: 'ig', name: 'Igbo', nativeName: 'Igbo', flag: '🇳🇬', region: 'Africa' },
  { code: 'zu-ZA', name: 'Zulu', nativeName: 'isiZulu', flag: '🇿🇦', region: 'Africa' },
  { code: 'xh', name: 'Xhosa', nativeName: 'isiXhosa', flag: '🇿🇦', region: 'Africa' },
  { code: 'af-ZA', name: 'Afrikaans', nativeName: 'Afrikaans', flag: '🇿🇦', region: 'Africa' },
  { code: 'st', name: 'Sesotho', nativeName: 'Sesotho', flag: '🇱🇸', region: 'Africa' },
  { code: 'tn', name: 'Tswana', nativeName: 'Setswana', flag: '🇧🇼', region: 'Africa' },
  { code: 'sn', name: 'Shona', nativeName: 'chiShona', flag: '🇿🇼', region: 'Africa' },
  { code: 'rw', name: 'Kinyarwanda', nativeName: 'Kinyarwanda', flag: '🇷🇼', region: 'Africa' },
  { code: 'ny', name: 'Chichewa', nativeName: 'Chichewa', flag: '🇲🇼', region: 'Africa' },
  { code: 'mg', name: 'Malagasy', nativeName: 'Malagasy', flag: '🇲🇬', region: 'Africa' },
  { code: 'so', name: 'Somali', nativeName: 'Soomaali', flag: '🇸🇴', region: 'Africa' },
  { code: 'ti', name: 'Tigrinya', nativeName: 'ትግርኛ', flag: '🇪🇷', region: 'Africa' },
  { code: 'om', name: 'Oromo', nativeName: 'Afaan Oromoo', flag: '🇪🇹', region: 'Africa' },
  { code: 'wo', name: 'Wolof', nativeName: 'Wolof', flag: '🇸🇳', region: 'Africa' },
  { code: 'ff', name: 'Fula', nativeName: 'Fulfulde', flag: '🇬🇳', region: 'Africa' },
  { code: 'ln', name: 'Lingala', nativeName: 'Lingála', flag: '🇨🇩', region: 'Africa' },
  { code: 'lg', name: 'Luganda', nativeName: 'Luganda', flag: '🇺🇬', region: 'Africa' },
  { code: 'ak', name: 'Akan/Twi', nativeName: 'Akan', flag: '🇬🇭', region: 'Africa' },
  { code: 'ee', name: 'Ewe', nativeName: 'Eʋegbe', flag: '🇬🇭', region: 'Africa' },
  { code: 'bm', name: 'Bambara', nativeName: 'Bamanankan', flag: '🇲🇱', region: 'Africa' },

  // ══════════════════════════════════════════
  // WAVE 10 — Américas (Indígenas + Crioulos)
  // ══════════════════════════════════════════
  { code: 'ht', name: 'Haitian Creole', nativeName: 'Kreyòl ayisyen', flag: '🇭🇹', region: 'Americas' },
  { code: 'qu', name: 'Quechua', nativeName: 'Runasimi', flag: '🇵🇪', region: 'Americas' },
  { code: 'gn', name: 'Guarani', nativeName: "Avañe'ẽ", flag: '🇵🇾', region: 'Americas' },
  { code: 'ay', name: 'Aymara', nativeName: 'Aymar aru', flag: '🇧🇴', region: 'Americas' },
  { code: 'nah', name: 'Nahuatl', nativeName: 'Nāhuatl', flag: '🇲🇽', region: 'Americas' },

  // ══════════════════════════════════════════
  // WAVE 11 — Oceania & Pacífico
  // ══════════════════════════════════════════
  { code: 'en-AU', name: 'English (Australia)', nativeName: 'English (Australia)', flag: '🇦🇺', region: 'Oceania' },
  { code: 'en-NZ', name: 'English (New Zealand)', nativeName: 'English (NZ)', flag: '🇳🇿', region: 'Oceania' },
  { code: 'mi', name: 'Maori', nativeName: 'Te Reo Māori', flag: '🇳🇿', region: 'Oceania' },
  { code: 'sm', name: 'Samoan', nativeName: 'Gagana Samoa', flag: '🇼🇸', region: 'Oceania' },
  { code: 'to', name: 'Tongan', nativeName: 'Lea fakatonga', flag: '🇹🇴', region: 'Oceania' },
  { code: 'fj', name: 'Fijian', nativeName: 'Vosa Vakaviti', flag: '🇫🇯', region: 'Oceania' },
  { code: 'haw', name: 'Hawaiian', nativeName: 'ʻŌlelo Hawaiʻi', flag: '🇺🇸', region: 'Oceania' },

  // ══════════════════════════════════════════
  // WAVE 12 — Línguas Celtas & Regionais Europeias
  // ══════════════════════════════════════════
  { code: 'ga-IE', name: 'Irish', nativeName: 'Gaeilge', flag: '🇮🇪', region: 'Europe' },
  { code: 'cy', name: 'Welsh', nativeName: 'Cymraeg', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿', region: 'Europe' },
  { code: 'gd', name: 'Scottish Gaelic', nativeName: 'Gàidhlig', flag: '🏴󠁧󠁢󠁳󠁣󠁴󠁿', region: 'Europe' },
  { code: 'br', name: 'Breton', nativeName: 'Brezhoneg', flag: '🇫🇷', region: 'Europe' },
  { code: 'co', name: 'Corsican', nativeName: 'Corsu', flag: '🇫🇷', region: 'Europe' },
  { code: 'oc', name: 'Occitan', nativeName: 'Occitan', flag: '🇫🇷', region: 'Europe' },
  { code: 'fy', name: 'Western Frisian', nativeName: 'Frysk', flag: '🇳🇱', region: 'Europe' },
  { code: 'sc', name: 'Sardinian', nativeName: 'Sardu', flag: '🇮🇹', region: 'Europe' },

  // ══════════════════════════════════════════
  // WAVE 13 — Outros Asiáticos & Cáucaso
  // ══════════════════════════════════════════
  { code: 'fil', name: 'Filipino (formal)', nativeName: 'Wikang Filipino', flag: '🇵🇭', region: 'Asia' },
  { code: 'hmn', name: 'Hmong', nativeName: 'Hmoob', flag: '🇱🇦', region: 'Asia' },
  { code: 'bo', name: 'Tibetan', nativeName: 'བོད་སྐད', flag: '🏔️', region: 'Asia' },
  { code: 'ug', name: 'Uyghur', nativeName: 'ئۇيغۇرچە', flag: '🏔️', region: 'Asia', rtl: true },
  { code: 'sd', name: 'Sindhi', nativeName: 'سنڌي', flag: '🇵🇰', region: 'Asia', rtl: true },
  { code: 'yi', name: 'Yiddish', nativeName: 'ייִדיש', flag: '🕎', region: 'Europe', rtl: true },

  // ══════════════════════════════════════════
  // WAVE 14 — Variantes Inglês & Espanhol Restantes
  // ══════════════════════════════════════════
  { code: 'en-IN', name: 'English (India)', nativeName: 'English (India)', flag: '🇮🇳', region: 'Asia' },
  { code: 'en-SG', name: 'English (Singapore)', nativeName: 'English (Singapore)', flag: '🇸🇬', region: 'Asia' },
  { code: 'en-ZA', name: 'English (South Africa)', nativeName: 'English (SA)', flag: '🇿🇦', region: 'Africa' },
  { code: 'en-PH', name: 'English (Philippines)', nativeName: 'English (PH)', flag: '🇵🇭', region: 'Asia' },
  { code: 'en-IE', name: 'English (Ireland)', nativeName: 'English (Ireland)', flag: '🇮🇪', region: 'Europe' },
  { code: 'en-CA', name: 'English (Canada)', nativeName: 'English (Canada)', flag: '🇨🇦', region: 'Americas' },
  { code: 'es-VE', name: 'Spanish (Venezuela)', nativeName: 'Español (Venezuela)', flag: '🇻🇪', region: 'Americas' },
  { code: 'es-EC', name: 'Spanish (Ecuador)', nativeName: 'Español (Ecuador)', flag: '🇪🇨', region: 'Americas' },
  { code: 'es-UY', name: 'Spanish (Uruguay)', nativeName: 'Español (Uruguay)', flag: '🇺🇾', region: 'Americas' },
  { code: 'es-PY', name: 'Spanish (Paraguay)', nativeName: 'Español (Paraguay)', flag: '🇵🇾', region: 'Americas' },
  { code: 'es-BO', name: 'Spanish (Bolivia)', nativeName: 'Español (Bolivia)', flag: '🇧🇴', region: 'Americas' },
  { code: 'es-CR', name: 'Spanish (Costa Rica)', nativeName: 'Español (Costa Rica)', flag: '🇨🇷', region: 'Americas' },
  { code: 'es-PA', name: 'Spanish (Panama)', nativeName: 'Español (Panamá)', flag: '🇵🇦', region: 'Americas' },
  { code: 'es-DO', name: 'Spanish (Dominican Rep.)', nativeName: 'Español (Rep. Dominicana)', flag: '🇩🇴', region: 'Americas' },
  { code: 'es-GT', name: 'Spanish (Guatemala)', nativeName: 'Español (Guatemala)', flag: '🇬🇹', region: 'Americas' },
  { code: 'es-HN', name: 'Spanish (Honduras)', nativeName: 'Español (Honduras)', flag: '🇭🇳', region: 'Americas' },
  { code: 'es-SV', name: 'Spanish (El Salvador)', nativeName: 'Español (El Salvador)', flag: '🇸🇻', region: 'Americas' },
  { code: 'es-NI', name: 'Spanish (Nicaragua)', nativeName: 'Español (Nicaragua)', flag: '🇳🇮', region: 'Americas' },
  { code: 'es-CU', name: 'Spanish (Cuba)', nativeName: 'Español (Cuba)', flag: '🇨🇺', region: 'Americas' },
  { code: 'es-PR', name: 'Spanish (Puerto Rico)', nativeName: 'Español (Puerto Rico)', flag: '🇵🇷', region: 'Americas' },
  { code: 'fr-SN', name: 'French (Senegal)', nativeName: 'Français (Sénégal)', flag: '🇸🇳', region: 'Africa' },
  { code: 'fr-CI', name: 'French (Ivory Coast)', nativeName: "Français (Côte d'Ivoire)", flag: '🇨🇮', region: 'Africa' },
  { code: 'fr-CM', name: 'French (Cameroon)', nativeName: 'Français (Cameroun)', flag: '🇨🇲', region: 'Africa' },
  { code: 'fr-CD', name: 'French (Congo)', nativeName: 'Français (Congo)', flag: '🇨🇩', region: 'Africa' },
  { code: 'ar-LY', name: 'Arabic (Libya)', nativeName: 'العربية (ليبيا)', flag: '🇱🇾', region: 'Africa', rtl: true },
  { code: 'ar-SD', name: 'Arabic (Sudan)', nativeName: 'العربية (السودان)', flag: '🇸🇩', region: 'Africa', rtl: true },
  { code: 'ar-SY', name: 'Arabic (Syria)', nativeName: 'العربية (سوريا)', flag: '🇸🇾', region: 'Middle East', rtl: true },
  { code: 'ar-YE', name: 'Arabic (Yemen)', nativeName: 'العربية (اليمن)', flag: '🇾🇪', region: 'Middle East', rtl: true },
  { code: 'ar-OM', name: 'Arabic (Oman)', nativeName: 'العربية (عمان)', flag: '🇴🇲', region: 'Middle East', rtl: true },
  { code: 'ar-QA', name: 'Arabic (Qatar)', nativeName: 'العربية (قطر)', flag: '🇶🇦', region: 'Middle East', rtl: true },
  { code: 'ar-KW', name: 'Arabic (Kuwait)', nativeName: 'العربية (الكويت)', flag: '🇰🇼', region: 'Middle East', rtl: true },
  { code: 'ar-BH', name: 'Arabic (Bahrain)', nativeName: 'العربية (البحرين)', flag: '🇧🇭', region: 'Middle East', rtl: true },
];

/** Total de idiomas */
export const TOTAL_LANGUAGES = SUPPORTED_LANGUAGES.length;

/** Idiomas RTL */
export const RTL_LANGUAGES = SUPPORTED_LANGUAGES.filter(l => l.rtl).map(l => l.code);

/** Buscar idioma por código */
export function getLanguage(code: string): Language | undefined {
  return SUPPORTED_LANGUAGES.find(l => l.code === code);
}

/** Agrupar por região */
export function getLanguagesByRegion(): Record<string, Language[]> {
  return SUPPORTED_LANGUAGES.reduce((acc, lang) => {
    if (!acc[lang.region]) acc[lang.region] = [];
    acc[lang.region].push(lang);
    return acc;
  }, {} as Record<string, Language[]>);
}

/** Verificar se é RTL */
export function isRTL(code: string): boolean {
  const lang = SUPPORTED_LANGUAGES.find(l => l.code === code);
  return lang?.rtl === true;
}
